/**
 * Pantalla "Horarios" con pestañas (008 · T043 · US1; contracts/ui.md §1.1).
 *
 * `SCHEDULE_TAB_IDS` es el catálogo completo de la URL (`?tab=cuadro|turnos|patrones|feriados|
 * ausencias`, defecto `cuadro`) y lo usa `routes/_app/horarios.tsx` en `validateSearch`: la ruta
 * no vuelve a cambiar. `SCHEDULE_TABS` es el **registro** de pestañas implementadas, en orden:
 * los lotes de Patrones (US5), Feriados y Ausencias (US4) solo agregan su entrada aquí. Una
 * pestaña del catálogo aún no registrada, o sin permiso, cae en la primera visible.
 */
import { CalendarClock } from 'lucide-react';

import type { MeResponse, Permission } from '@fermin/contracts';

import { EmptyState } from '../../components/ui/empty-state';
import { SkeletonLines } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { t } from '../../i18n';
import { AbsencesTab } from '../absences/absences-tab';
import { hasPermission, useMe } from '../shell/use-me';

import { GridTab } from './grid/grid-tab';
import { HolidaysTab } from './holidays/holidays-tab';
import { PatternsTab } from './patterns/patterns-tab';
import { TemplatesTab } from './templates/templates-tab';

import type { ComponentType } from 'react';

/** Pestañas que admite la URL (contracts/ui.md §1.1). */
export const SCHEDULE_TAB_IDS = ['cuadro', 'turnos', 'patrones', 'feriados', 'ausencias'] as const;
export type ScheduleTab = (typeof SCHEDULE_TAB_IDS)[number];

export const DEFAULT_SCHEDULE_TAB: ScheduleTab = 'cuadro';

/** `true` si el valor es una pestaña del catálogo. */
export function isScheduleTab(value: unknown): value is ScheduleTab {
  return typeof value === 'string' && (SCHEDULE_TAB_IDS as readonly string[]).includes(value);
}

/** Props que recibe cada pestaña: puede pedir cambiar a otra (p. ej. "Ir a Turnos"). */
export interface ScheduleTabProps {
  onNavigate: (tab: ScheduleTab) => void;
}

export interface ScheduleTabDefinition {
  id: ScheduleTab;
  /** Clave i18n de la etiqueta. */
  labelKey: string;
  /** Permiso adicional a `schedules.read` (que ya exige el ítem de menú). */
  permission?: Permission;
  component: ComponentType<ScheduleTabProps>;
}

/** Cuadro de turnos: su estado vacío lleva a Turnos. */
function GridTabEntry({ onNavigate }: ScheduleTabProps) {
  return <GridTab onGoToTemplates={() => onNavigate('turnos')} />;
}

/** Registro de pestañas implementadas, en orden de aparición. */
export const SCHEDULE_TABS: readonly ScheduleTabDefinition[] = [
  {
    id: 'cuadro',
    labelKey: 'schedules.tabs.cuadro',
    component: GridTabEntry,
  },
  { id: 'turnos', labelKey: 'schedules.tabs.turnos', component: TemplatesTab },
  // US5 (T087): patrones · US4 (T075/T076): feriados y ausencias. Todas se leen con
  // `schedules.read`; las acciones las ocultan las propias pestañas según permiso.
  { id: 'patrones', labelKey: 'schedules.tabs.patrones', component: PatternsTab },
  { id: 'feriados', labelKey: 'schedules.tabs.feriados', component: HolidaysTab },
  { id: 'ausencias', labelKey: 'schedules.tabs.ausencias', component: AbsencesTab },
];

/** Pestañas registradas visibles para la sesión. */
export function visibleScheduleTabs(me: MeResponse | undefined): ScheduleTabDefinition[] {
  if (!hasPermission(me, 'schedules.read')) {
    return [];
  }
  return SCHEDULE_TABS.filter(
    (tab) => tab.permission === undefined || hasPermission(me, tab.permission),
  );
}

export interface SchedulesPageProps {
  tab?: ScheduleTab;
  onTabChange?: (tab: ScheduleTab) => void;
}

export function SchedulesPage({ tab = DEFAULT_SCHEDULE_TAB, onTabChange }: SchedulesPageProps) {
  const me = useMe();

  if (me.isPending) {
    return <SkeletonLines lines={4} />;
  }

  const tabs = visibleScheduleTabs(me.data);
  const active = tabs.find((entry) => entry.id === tab) ?? tabs[0];

  return (
    <section className="flex flex-col gap-fm-4">
      <header>
        <h1 className="font-display text-fm-2xl font-bold text-fm-text">
          {t('schedules.page.title')}
        </h1>
        <p className="mt-fm-1 text-fm-sm text-fm-text-secondary">{t('schedules.page.subtitle')}</p>
      </header>

      {active === undefined ? (
        <EmptyState icon={CalendarClock} title={t('schedules.page.noAccess')} />
      ) : (
        <Tabs value={active.id} onValueChange={(next) => onTabChange?.(next as ScheduleTab)}>
          <TabsList aria-label={t('schedules.tabs.label')}>
            {tabs.map((entry) => (
              <TabsTrigger
                key={entry.id}
                value={entry.id}
                data-testid={`schedules-tab-${entry.id}`}
              >
                {t(entry.labelKey)}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((entry) => (
            <TabsContent key={entry.id} value={entry.id}>
              {entry.id === active.id ? (
                <entry.component onNavigate={(next) => onTabChange?.(next)} />
              ) : null}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </section>
  );
}
